import type { SectionId } from '../core/choreography'
import { prefersReducedMotion } from '../core/env'
import { store } from '../core/store'
import { translate } from '../i18n'
import { playBotClickSound } from '../core/sound'

/**
 * The CRAFT TOWER agent — a small hovering robot with a tablet that follows the visitor.
 * Each section has its own pose (CSS-driven, see styles/agent.css) and a one-line tip.
 * Pure SVG + CSS: no images, themable, and a glowing wireframe in Cyber mode.
 */

const POSE: Record<SectionId, string> = {
  home: 'wave',
  services: 'present',
  work: 'browse',
  process: 'type',
  about: 'heart',
  contact: 'send',
}

// Heart shape centred on the origin, reused for the floating heart and the tablet screen.
const HEART = 'M0 -3c-2.5-3.6-8-2-7 2.6c.8 3.6 7 7.4 7 7.4s6.2-3.8 7-7.4c1-4.6-4.5-6.2-7-2.6z'

const SVG = `
<svg class="ag" viewBox="0 0 140 160" aria-hidden="true" focusable="false" data-pose="wave">
  <defs>
    <linearGradient id="ag-shell-grad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" class="ag-stop-hi"/>
      <stop offset="1" class="ag-stop-lo"/>
    </linearGradient>
  </defs>
  <ellipse class="ag-shadow" cx="70" cy="152" rx="26" ry="4.5"/>
  <g class="ag-float">
    <ellipse class="ag-jet" cx="70" cy="131" rx="13" ry="3.6"/>
    <path class="ag-heart" d="${HEART}"/>
    <rect class="ag-body" x="50" y="76" width="40" height="52" rx="18"/>
    <path class="ag-seam" d="M54 106h32"/>
    <circle class="ag-core" cx="70" cy="93" r="4.5"/>
    <g class="ag-arm ag-arm--l"><g class="ag-arm-inner">
      <rect class="ag-limb" x="47" y="84" width="10" height="25" rx="5"/>
      <circle class="ag-hand" cx="52" cy="110" r="6"/>
    </g></g>
    <g class="ag-head">
      <path class="ag-antenna" d="M70 27V16"/>
      <circle class="ag-bulb" cx="70" cy="12.5" r="4.2"/>
      <rect class="ag-ear" x="34" y="43" width="7" height="17" rx="3.5"/>
      <rect class="ag-ear" x="99" y="43" width="7" height="17" rx="3.5"/>
      <rect class="ag-shell" x="39" y="26" width="62" height="50" rx="19"/>
      <rect class="ag-face" x="46" y="33" width="48" height="36" rx="13"/>
      <g class="ag-eyes">
        <g class="ag-eyes-open">
          <rect class="ag-eye" x="56.5" y="44" width="7" height="12" rx="3.5"/>
          <rect class="ag-eye" x="76.5" y="44" width="7" height="12" rx="3.5"/>
        </g>
        <g class="ag-eyes-happy">
          <path class="ag-stroke" d="M56 52q4-6 8 0"/>
          <path class="ag-stroke" d="M76 52q4-6 8 0"/>
        </g>
      </g>
      <path class="ag-stroke ag-mouth" d="M66 61q4 3.2 8 0"/>
      <circle class="ag-cheek" cx="52.5" cy="59" r="2.8"/>
      <circle class="ag-cheek" cx="87.5" cy="59" r="2.8"/>
    </g>
    <g class="ag-tablet">
      <rect class="ag-tab" x="-16" y="-21" width="32" height="42" rx="5"/>
      <rect class="ag-screen" x="-13" y="-18" width="26" height="33" rx="2.5"/>
      <g class="ag-scr ag-scr--code">
        <path class="ag-scr-stroke" d="M-5-6l-5 4.5 5 4.5M5-6l5 4.5-5 4.5M2-8l-4 13"/>
      </g>
      <g class="ag-scr ag-scr--cards">
        <rect class="ag-scr-fill" x="-10" y="-14" width="9" height="11" rx="1.5"/>
        <rect class="ag-scr-dim" x="1" y="-14" width="9" height="11" rx="1.5"/>
        <rect class="ag-scr-dim" x="-10" y="0" width="9" height="11" rx="1.5"/>
        <rect class="ag-scr-dim" x="1" y="0" width="9" height="11" rx="1.5"/>
      </g>
      <g class="ag-scr ag-scr--image">
        <rect class="ag-scr-dim" x="-10" y="-14" width="20" height="18" rx="1.5"/>
        <circle class="ag-scr-fill" cx="5" cy="-9" r="2.6"/>
        <path class="ag-scr-fill" d="M-10 4l7-9 5 5 3-3 5 7z"/>
        <rect class="ag-scr-dim" x="-10" y="7.5" width="13" height="2.4" rx="1.2"/>
      </g>
      <g class="ag-scr ag-scr--progress">
        <rect class="ag-scr-dim" x="-10" y="-13" width="14" height="2.4" rx="1.2"/>
        <rect class="ag-scr-dim" x="-10" y="-8" width="19" height="2.4" rx="1.2"/>
        <rect class="ag-scr-dim" x="-10" y="0" width="20" height="5" rx="2.5"/>
        <rect class="ag-scr-fill ag-progress" x="-10" y="0" width="20" height="5" rx="2.5"/>
      </g>
      <g class="ag-scr ag-scr--heart"><path class="ag-scr-fill" d="${HEART}"/></g>
      <g class="ag-scr ag-scr--mail">
        <rect class="ag-scr-dim" x="-10" y="-8" width="20" height="14" rx="2"/>
        <path class="ag-scr-stroke" d="M-9-7l9 7 9-7"/>
      </g>
      <rect class="ag-tab-bar" x="-4" y="17.4" width="8" height="1.6" rx=".8"/>
    </g>
    <g class="ag-arm ag-arm--r"><g class="ag-arm-inner">
      <rect class="ag-limb" x="83" y="84" width="10" height="25" rx="5"/>
      <circle class="ag-hand" cx="88" cy="110" r="6"/>
    </g></g>
    <path class="ag-plane" d="M0 0L13-6 6 5zM6 5l.5-6"/>
  </g>
</svg>`

export class Agent {
  private root = document.createElement('div')
  private button: HTMLButtonElement
  private badge: HTMLElement
  private svg: SVGSVGElement
  private bubble: HTMLElement
  private textEl: HTMLElement
  private section: SectionId = 'home'
  private seen = new Set<SectionId>()
  private started = false
  private wasOpened = false
  private showTimer = 0
  private hideTimer = 0
  private footerTimer = 0
  private lastProgress = -1
  private customText = ''
  private onNavigateContact?: () => void
  private onOpenChat?: () => void

  constructor(
    private readonly line: (section: SectionId) => string,
    callbacks?: {
      onNavigateContact?: () => void
      onOpenChat?: () => void
    },
  ) {
    this.onNavigateContact = callbacks?.onNavigateContact
    this.onOpenChat = callbacks?.onOpenChat

    this.root.className = 'agent'
    this.root.setAttribute('aria-label', 'Craft Tower Companion Agent')
    this.root.innerHTML = `
      <div class="agent__bubble">
        <div class="agent__bubble-header">
          <span class="mono agent__bubble-title">${this.t('agent.badgeText')}</span>
          <span class="agent__bubble-status mono"><span class="agent__live-dot"></span>${this.t('agent.onlineStatus')}</span>
          <button type="button" class="agent__bubble-close" aria-label="Close">×</button>
        </div>
        <p class="agent__bubble-text"></p>
        <div class="agent__bubble-actions">
          <button type="button" class="agent__action-btn agent__action-btn--contact">
            <span>${this.t('agent.sendMessage')}</span>
            <svg width="12" height="12" aria-hidden="true"><use href="#i-arrow"/></svg>
          </button>
          <button type="button" class="agent__action-btn agent__action-btn--chat">
            <svg width="12" height="12" aria-hidden="true"><use href="#i-chat"/></svg>
            <span>${this.t('agent.openChat')}</span>
          </button>
        </div>
      </div>
      <button type="button" class="agent__btn" aria-label="Craft Tower Agent">
        ${SVG}
        <span class="agent__badge" hidden></span>
      </button>
    `
    document.body.append(this.root)
    this.button = this.root.querySelector('.agent__btn')!
    this.badge = this.root.querySelector('.agent__badge')!
    this.svg = this.root.querySelector('svg')!
    this.bubble = this.root.querySelector('.agent__bubble')!
    this.textEl = this.root.querySelector('.agent__bubble-text')!

    this.bindEvents()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.onSection((entry.target as HTMLElement).dataset.section as SectionId)
        }
      },
      { rootMargin: '-45% 0px -50% 0px' },
    )
    document.querySelectorAll('[data-section]').forEach((section) => observer.observe(section))

    const footerTarget = document.querySelector('.site-footer .footer__bottom') ?? document.querySelector('.site-footer')
    if (footerTarget) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (this.wasOpened) return
              window.clearTimeout(this.footerTimer)
              this.footerTimer = window.setTimeout(() => {
                if (!this.wasOpened) {
                  this.wasOpened = true
                  this.hop()
                  this.say(true)
                }
              }, 1000)
            } else {
              window.clearTimeout(this.footerTimer)
            }
          }
        },
        { threshold: 0.1 },
      )
      footerObserver.observe(footerTarget)
    }
  }

  private t(key: string): string {
    return translate(store.get().lang, key, store.get().cyber)
  }

  private bindEvents(): void {
    this.button.addEventListener('click', () => {
      this.hop()
      playBotClickSound(this.section, store.get().cyber)
      if (this.bubble.classList.contains('is-visible')) {
        this.hide()
      } else {
        this.wasOpened = true
        window.clearTimeout(this.footerTimer)
        this.say(true)
      }
    })

    const closeBtn = this.bubble.querySelector('.agent__bubble-close')
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hide()
    })

    const contactBtn = this.bubble.querySelector('.agent__action-btn--contact')
    contactBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hide()
      if (this.onNavigateContact) {
        this.onNavigateContact()
      } else {
        const contactSec = document.getElementById('contact')
        contactSec?.scrollIntoView({ behavior: 'smooth' })
      }
    })

    const chatBtn = this.bubble.querySelector('.agent__action-btn--chat')
    chatBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.hide()
      this.onOpenChat?.()
    })
  }

  setCallbacks(callbacks: { onNavigateContact?: () => void; onOpenChat?: () => void }): void {
    if (callbacks.onNavigateContact) this.onNavigateContact = callbacks.onNavigateContact
    if (callbacks.onOpenChat) this.onOpenChat = callbacks.onOpenChat
  }

  /** Fly in once the preloader has lifted. Dialog is never opened automatically on start. */
  start(): void {
    this.started = true
    this.svg.dataset.pose = POSE[this.section]
    this.root.classList.add('is-in')
  }

  /** Mirrors the Process progress on the tablet's progress bar. */
  setProgress(progress: number): void {
    if (Math.abs(progress - this.lastProgress) < 0.005) return
    this.lastProgress = progress
    this.svg.style.setProperty('--p', progress.toFixed(3))
  }

  /** Set unread count badge on mascot */
  setUnreadCount(count: number, { announce = true }: { announce?: boolean } = {}): void {
    if (count > 0) {
      this.badge.hidden = false
      this.badge.textContent = String(count > 9 ? '9+' : count)
      // Keep the accessible name in sync with the visible badge (WCAG 2.5.3 label in name).
      this.button.setAttribute('aria-label', `Craft Tower Agent (${this.badge.textContent})`)
      if (!announce) {
        this.customText = ''
        return
      }
      this.hop()
      this.customText = this.t('chat.unreadMsg')
      this.wasOpened = true
      window.clearTimeout(this.footerTimer)
      this.say(true)
    } else {
      this.badge.hidden = true
      this.button.setAttribute('aria-label', 'Craft Tower Agent')
      this.customText = ''
    }
  }

  /** Alert user when cookie banner appears */
  notifyCookieBanner(): void {
    this.hop()
    this.svg.dataset.pose = 'heart'
    window.setTimeout(() => {
      if (this.section) this.svg.dataset.pose = POSE[this.section]
    }, 4000)
  }

  /** Reaction when user accepts/saves cookies */
  notifyCookieAccepted(): void {
    this.hop()
    this.svg.dataset.pose = 'wave'
    window.setTimeout(() => {
      if (this.section) this.svg.dataset.pose = POSE[this.section]
    }, 2000)
  }

  /** Re-translate visible strings after language or theme switch. */
  refresh(): void {
    const titleEl = this.bubble.querySelector('.agent__bubble-title')
    if (titleEl) titleEl.textContent = this.t('agent.badgeText')
    const statusEl = this.bubble.querySelector('.agent__bubble-status')
    if (statusEl) statusEl.innerHTML = `<span class="agent__live-dot"></span>${this.t('agent.onlineStatus')}`
    const contactBtnSpan = this.bubble.querySelector('.agent__action-btn--contact span')
    if (contactBtnSpan) contactBtnSpan.textContent = this.t('agent.sendMessage')
    const chatBtnSpan = this.bubble.querySelector('.agent__action-btn--chat span')
    if (chatBtnSpan) chatBtnSpan.textContent = this.t('agent.openChat')

    if (this.bubble.classList.contains('is-visible')) {
      this.textEl.textContent = this.customText || this.line(this.section)
    }
  }

  private onSection(id: SectionId): void {
    if (!id || id === this.section) return
    this.section = id
    this.customText = ''
    this.svg.dataset.pose = POSE[id]
    if (!this.started) return
    this.hop()
    this.hide()
    window.clearTimeout(this.showTimer)
  }

  private say(force = false): void {
    if (!force && this.seen.has(this.section)) return
    this.seen.add(this.section)
    this.wasOpened = true
    window.clearTimeout(this.footerTimer)
    this.textEl.textContent = this.customText || this.line(this.section)
    this.bubble.classList.add('is-visible')
    this.root.classList.add('is-talking')
    window.clearTimeout(this.hideTimer)
    this.hideTimer = window.setTimeout(() => this.hide(), 9000)
  }

  private hide(): void {
    this.bubble.classList.remove('is-visible')
    this.root.classList.remove('is-talking')
    this.customText = ''
  }

  hop(): void {
    if (prefersReducedMotion()) return
    this.button.classList.remove('is-hop')
    void this.button.offsetWidth
    this.button.classList.add('is-hop')
  }

  setGyro(roll: number, pitch: number): void {
    if (prefersReducedMotion()) return
    // Subtle physical 3D pitch-and-roll parallax on the companion mascot
    const rx = (-pitch * 12).toFixed(2)
    const ry = (roll * 14).toFixed(2)
    const tx = (roll * 6).toFixed(1)
    const ty = (pitch * 5).toFixed(1)
    this.button.style.transform = `perspective(500px) rotateX(${rx}deg) rotateY(${ry}deg) translate3d(${tx}px, ${ty}px, 0)`
  }

  resetGyro(): void {
    this.button.style.transform = ''
  }
}
